import type { BackgroundCheckStatus } from '@church/domain'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useApi } from '../api-context.tsx'
import { checkinKeys, type CheckinLocation } from './useCheckin.ts'

/**
 * The rest of Sunday morning: who serves in a room, whose background check is
 * current, who may collect a child, paging a parent, and the register after.
 */

export const checkinAdminKeys = {
  volunteers: (locationId: string) => [...checkinKeys.all(), 'volunteers', locationId] as const,
  checks: (status: string | null) => [...checkinKeys.all(), 'checks', status] as const,
  check: (personId: string) => [...checkinKeys.all(), 'check', personId] as const,
  collectors: (checkinId: string) => [...checkinKeys.all(), 'collectors', checkinId] as const,
  history: (day: string, locationId: string | null) =>
    [...checkinKeys.all(), 'history', day, locationId] as const,
  daily: (from: string, to: string) => [...checkinKeys.all(), 'daily', from, to] as const,
}

// ---- volunteers -----------------------------------------------------------------

export function useCheckinVolunteers(locationId: string) {
  const api = useApi()
  return useQuery({
    queryKey: checkinAdminKeys.volunteers(locationId),
    enabled: locationId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.checkin
        .locations({ id: locationId })
        .volunteers.get()
      if (error) throw error
      return data
    },
  })
}

export type CheckinVolunteer = NonNullable<ReturnType<typeof useCheckinVolunteers>['data']>[number]

export function useAssignVolunteer(locationId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { personId: string; role?: 'leader' | 'helper' }) => {
      const { data, error } = await api.api.v1.checkin
        .locations({ id: locationId })
        .volunteers.post(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => queryClient.setQueryData(checkinAdminKeys.volunteers(locationId), data),
  })
}

export function useRemoveVolunteer(locationId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (assignmentId: string) => {
      const { error } = await api.api.v1.checkin
        .locations({ id: locationId })
        .volunteers({ assignmentId })
        .delete()
      if (error) throw error
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: checkinAdminKeys.volunteers(locationId) }),
  })
}

// ---- background checks ---------------------------------------------------------

export function useBackgroundChecks(status: BackgroundCheckStatus | null) {
  const api = useApi()
  return useInfiniteQuery({
    queryKey: checkinAdminKeys.checks(status),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1.checkin['background-checks'].get({
        query: { ...(status ? { status } : {}), ...(pageParam ? { cursor: pageParam } : {}) },
      })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
  })
}

export function useBackgroundCheck(personId: string) {
  const api = useApi()
  return useQuery({
    queryKey: checkinAdminKeys.check(personId),
    queryFn: async () => {
      const { data, error } = await api.api.v1.checkin['background-checks']({ personId }).get()
      if (error) throw error
      return data
    },
  })
}

export type BackgroundCheckRecord = NonNullable<ReturnType<typeof useBackgroundCheck>['data']>

export function useRecordBackgroundCheck(personId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      status: BackgroundCheckStatus
      provider?: string | null
      completedAt?: string | null
      expiresAt?: string | null
      requestedAt?: string | null
      note?: string | null
    }) => {
      const { data, error } = await api.api.v1.checkin['background-checks']({ personId }).put(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(checkinAdminKeys.check(personId), data)
      void queryClient.invalidateQueries({ queryKey: [...checkinKeys.all(), 'checks'] })
      void queryClient.invalidateQueries({ queryKey: [...checkinKeys.all(), 'volunteers'] })
    },
  })
}

// ---- handing a child over ------------------------------------------------------

/**
 * The check-out at a room door: who may collect, the code, and the
 * supervisor path. The screen renders a list of names and a code box; every
 * rule lives on the server and in `decideCollection`.
 */
export function useCheckoutFlow(checkinId: string, enabled: boolean) {
  const api = useApi()
  const queryClient = useQueryClient()
  const collectors = useQuery({
    queryKey: checkinAdminKeys.collectors(checkinId),
    enabled,
    queryFn: async () => {
      const { data, error } = await api.api.v1.checkin({ id: checkinId }).collectors.get()
      if (error) throw error
      return data
    },
  })

  const checkout = useMutation({
    mutationFn: async (body: {
      securityCode?: string | null
      collectorPersonId?: string | null
      collectorName?: string | null
      supervisorOverride?: boolean
      note?: string | null
    }) => {
      const { data, error } = await api.api.v1.checkin({ id: checkinId }).checkout.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: checkinKeys.all() }),
  })

  const page = useMutation({
    mutationFn: async (message: string) => {
      const { data, error } = await api.api.v1.checkin({ id: checkinId }).page.post({ message })
      if (error) throw error
      return data
    },
  })

  return {
    collectors: collectors.data ?? [],
    collectorsLoading: collectors.isPending && enabled,
    checkout,
    page,
  }
}

// ---- the register ----------------------------------------------------------------

export function useCheckinHistory(day: string, locationId: string | null) {
  const api = useApi()
  return useInfiniteQuery({
    queryKey: checkinAdminKeys.history(day, locationId),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1.checkin.history.get({
        query: {
          day,
          limit: 100,
          ...(locationId ? { locationId } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
  })
}

export function useCheckinDailyReport(from: string, to: string) {
  const api = useApi()
  return useQuery({
    queryKey: checkinAdminKeys.daily(from, to),
    queryFn: async () => {
      const { data, error } = await api.api.v1.checkin.reports.daily.get({ query: { from, to } })
      if (error) throw error
      return data
    },
  })
}

// ---- live counts ----------------------------------------------------------------

interface CountsMessage {
  type: 'counts'
  counts: Record<string, number>
}

const isCounts = (value: unknown): value is CountsMessage =>
  typeof value === 'object' &&
  value !== null &&
  (value as { type?: unknown }).type === 'counts' &&
  typeof (value as { counts?: unknown }).counts === 'object'

/**
 * Room counts pushed over a WebSocket the moment a check-in or check-out
 * commits anywhere in the church, folded straight into the cached room list.
 *
 * `url` comes from the platform (the web derives it from its own origin).
 * Reconnects with a backoff; while the socket is down the rooms query's own
 * 10-second poll keeps the numbers honest, so this only ever makes them
 * fresher. Returns whether the socket is currently connected.
 */
export function useLiveCheckinCounts(url: string | null): boolean {
  const queryClient = useQueryClient()
  const [connected, setConnected] = useState(false)

  useEffect(() => {
    if (!url) return
    let socket: WebSocket | null = null
    let retry: ReturnType<typeof setTimeout> | null = null
    let attempt = 0
    let closed = false

    const connect = () => {
      socket = new WebSocket(url)
      socket.onopen = () => {
        attempt = 0
        setConnected(true)
      }
      socket.onmessage = (event) => {
        let parsed: unknown
        try {
          parsed = JSON.parse(String(event.data))
        } catch {
          return
        }
        if (!isCounts(parsed)) return
        const counts = parsed.counts
        queryClient.setQueriesData<CheckinLocation[]>(
          { queryKey: [...checkinKeys.all(), 'locations'] },
          (rooms) =>
            rooms?.map((room) => {
              const checkedIn = counts[room.id] ?? 0
              const remaining =
                room.capacity === null ? null : Math.max(room.capacity - checkedIn, 0)
              return { ...room, checkedIn, remaining, isFull: remaining === 0 }
            }),
        )
      }
      socket.onclose = () => {
        setConnected(false)
        if (closed) return
        attempt += 1
        retry = setTimeout(connect, Math.min(30_000, 1_000 * 2 ** attempt))
      }
    }
    connect()

    return () => {
      closed = true
      if (retry) clearTimeout(retry)
      socket?.close()
    }
  }, [queryClient, url])

  return connected
}

export function useRevokeStation() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (stationId: string) => {
      const { error } = await api.api.v1.checkin.stations({ id: stationId }).delete()
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: checkinKeys.all() }),
  })
}
